import { useEffect, useMemo } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { formatForDatetimeLocal } from '../../../../lib/formatForDatetimeLocal.js';
import styles from './FieldTypesView.module.css';

const TextBox = ({ field, selectedLog }) => {
    const { id, name, required } = field;
    return (
        <div className={styles.inputGroup}>
            <label htmlFor={id} className={styles.label}>{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <input id={id} type='text' className={styles.input} maxLength='100' defaultValue={selectedLog[id]} placeholder='Your answer' disabled readOnly />
        </div>
    );
};

const Email = ({ field, selectedLog }) => {
    const { id, name, required } = field;
    return (
        <div className={styles.inputGroup}>
            <label htmlFor={id} className={styles.label}>{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <input id={id} type='email' className={styles.input} maxLength='100' defaultValue={selectedLog[id] || ''} placeholder='example@email.com' disabled readOnly />
        </div>
    );
};

const Phone = ({ field, selectedLog }) => {
    const { id, name, required } = field;
    return (
        <div className={styles.inputGroup}>
            <label htmlFor={id} className={styles.label}>{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <input id={id} type='tel' className={styles.input} maxLength='100' defaultValue={selectedLog[id] || ''} placeholder='(123) 456-7890' disabled readOnly />
        </div>
    );
};

const Address = ({ field, selectedLog }) => {
    const { id, name, required } = field;
    const addressValue = selectedLog[id] || {};
    
    const formatAddress = () => {
        const parts = [];
        if (addressValue.street) parts.push(addressValue.street);
        if (addressValue.city) parts.push(addressValue.city);
        if (addressValue.state && addressValue.postalCode) {
            parts.push(`${addressValue.state} ${addressValue.postalCode}`);
        } else if (addressValue.state) {
            parts.push(addressValue.state);
        } else if (addressValue.postalCode) {
            parts.push(addressValue.postalCode);
        }
        return parts.join(', ') || 'No address provided';
    };
    
    return (
        <div className={styles.inputGroup}>
            <label className={styles.label}>{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <p className={styles.value}>{formatAddress()}</p>
        </div>
    );
};

const TextArea = ({ field, selectedLog }) => {
    const { id, name, required } = field;
    return (
        <div className={styles.inputGroup}>
            <label htmlFor={id} className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <textarea id={id} defaultValue={selectedLog[id]} className={styles.textarea} placeholder='Your answer' maxLength={256} disabled readOnly style={field.styles || {}} />
        </div>
    );
};

const Number = ({ field, selectedLog }) => {
    const { id, name, required } = field;
    return (
        <div className={styles.inputGroup}>
            <label htmlFor={id} className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <input id={id} type='number' placeholder={0} className={styles.input} defaultValue={selectedLog[id]} disabled readOnly />
        </div>
    );
};

const Timestamp = ({ field, selectedLog, isTimestamp }) => {
    const { id, name, required } = field;

    return (
        <div className={styles.inputGroup}>
            <label htmlFor={id} className={styles.label}>{(required && !isTimestamp) ? <>{name}<sup><i className='far fa-asterisk' /></sup></> : name}</label>
            <input id={id} type='datetime-local' className={styles.input} defaultValue={selectedLog[id] ? formatForDatetimeLocal(new Date(selectedLog[id])) : ''} disabled readOnly />
        </div>
    );
};

const Dropdown = ({ field, selectedLog }) => {
    const { id, options, name, required } = field;
    return (
        <div className={styles.inputGroupSelect}>
            <label htmlFor={id} className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <select id={id} defaultValue={selectedLog[id] ?? ''} className={styles.dropdown} readOnly>
                <option value='' disabled>Select an option</option>
                {options?.map((name, index) => (<option key={name + index} className={styles.option} value={name}>{name}</option>))}
            </select>
        </div>
    );
};

const Signature = ({ field, sigCanvas, setSigCanvas, selectedLog }) => {
    const { id, name, required } = field;

    useEffect(() => {
        if (sigCanvas) {
            sigCanvas.off();
        };
    },[sigCanvas]);

    return (
        <div className={styles.inputGroup}>
            <div className={styles.signatureLabelContainer}>
                <label className={styles.label}>{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            </div>
            {selectedLog[id] ? <SignatureCanvas ref={(ref) => { setSigCanvas(ref) }} penColor='black' canvasProps={{ width: 350, height: 100, className: `${styles.trackerCanvas}` }} /> : <p>No Signature</p>}
        </div>
    );
};

const SingleSelect = ({ field, selectedLog }) => {
    const { id, name, options, required } = field;
    return (
        <div className={styles.inputGroup}>
            <label className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            {options.map((opt, index) => (
                <div className={styles.singleSelectGroup} key={opt + index}>
                    <label htmlFor={id+index} className={styles.singleSelect}>
                        <input 
                            type='radio' 
                            name={`${field}_options`} 
                            id={id+index} 
                            value={opt} 
                            defaultChecked={selectedLog[id] === opt} 
                            readOnly
                        />
                        {selectedLog[id] === opt && <i className='fas fa-circle' />}
                    </label>
                    {opt}
                </div>
            ))}
        </div>
    );
};

const Checklist = ({ field, selectedLog }) => {
    const { id, name, options, required } = field;
    return (
        <div className={styles.inputGroup}>
            <label className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            {options?.map((opt, index) => (
                <div className={styles.checkboxGroup} key={opt + index}>
                    <label className={styles.checkbox}>
                        {selectedLog[id]?.includes(opt) && <i className='fas fa-check' />}
                        <input 
                            type='checkbox' 
                            id={id+index} 
                            defaultChecked={selectedLog[id]?.includes(opt) ?? false} 
                            disabled
                            readOnly
                        />
                    </label>
                    {opt}
                </div>
            ))}
        </div>
    );
};

const Checkbox = ({ field, selectedLog }) => {
    const { id, name, required } = field;
    return (
        <div className={styles.inputGroup} style={{flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', gap: '0 .5rem'}}>
            <label htmlFor={id} className={styles.checkbox}>
                {selectedLog[id] && <i className='fas fa-check' />}
                <input type='checkbox' id={id} className={styles.checkbox} defaultChecked={selectedLog[id] || false} disabled readOnly />
            </label>
            <label className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
        </div>
    );
};

const IconRating = ({ field, selectedLog }) => {
    const { id, name, required, icon } = field;

    const iconObj = useMemo(() => {
        const obj = {};
        switch (icon) {
            case 'hearts':
                obj.unselected = 'far fa-heart';
                obj.selected = 'fas fa-heart';
                obj.checked = 'red';
                break;
            case 'thumbs-up':
                obj.unselected = 'far fa-thumbs-up';
                obj.selected = 'fas fa-thumbs-up';
                obj.checked = '#161d2d';
                break;
            case 'flames':
                obj.unselected = 'far fa-fire';
                obj.selected = 'fa-duotone fa-fire';
                obj.checked = '#e14714';
                obj.checked2 = '#ff9b29';
                break;
            case 'emotions':
                obj.unselected = 'far fa-face-meh';
                obj.selected = 'far fa-face-angry';
                obj.selected2 = 'far fa-face-frown';
                obj.selected3 = 'far fa-face-grin';
                obj.selected4 = 'far fa-face-grin-wide';
                obj.selected5 = 'far fa-face-laugh-beam';
                obj.checked = 'red';
                obj.checked2 = 'orangered';
                obj.checked3 = 'purple';
                obj.checked4 = 'blue';
                obj.checked5 = 'green';
                break;
            case 'stars':
            default:
                obj.unselected = 'far fa-star';
                obj.selected = 'fas fa-star';
                obj.checked = 'gold';
                break;
        };

        return obj;
    },[icon]);

    return (
        <div className={styles.inputGroup}>
            <label className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <div className={styles.icons}>
                <div id='icon-1' className={selectedLog[id] === 1 ? `${styles.iconRating} ${styles.selected}` : styles.iconRating}>
                    <i className={selectedLog[id] > 0 ? iconObj.selected : iconObj.unselected} style={selectedLog[id] > 0 ? icon === 'flames' ? {'--fa-primary-color': iconObj.checked, '--fa-secondary-color': iconObj.checked2, '--fa-secondary-opacity': '1'} : {color: iconObj.checked} : null} />
                </div>
                <div id='icon-2' className={selectedLog[id] === 2 ? `${styles.iconRating} ${styles.selected}` : styles.iconRating}>
                    <i className={selectedLog[id] > 1 ? icon === 'emotions' ? iconObj.selected2 : iconObj.selected : iconObj.unselected} style={selectedLog[id] > 1 ? icon === 'flames' ? {'--fa-primary-color': iconObj.checked, '--fa-secondary-color': iconObj.checked2, '--fa-secondary-opacity': '1'} : icon === 'emotions' ? {color: iconObj.checked2} : {color: iconObj.checked} : null} />
                </div>
                <div id='icon-3' className={selectedLog[id] === 3 ? `${styles.iconRating} ${styles.selected}` : styles.iconRating}>
                    <i className={selectedLog[id] > 2 ? icon === 'emotions' ? iconObj.selected3 : iconObj.selected : iconObj.unselected} style={selectedLog[id] > 2 ? icon === 'flames' ? {'--fa-primary-color': iconObj.checked, '--fa-secondary-color': iconObj.checked2, '--fa-secondary-opacity': '1'} : icon === 'emotions' ? {color: iconObj.checked3} : {color: iconObj.checked} : null} />
                </div>
                <div id='icon-4' className={selectedLog[id] === 4 ? `${styles.iconRating} ${styles.selected}` : styles.iconRating}>
                    <i className={selectedLog[id] > 3 ? icon === 'emotions' ? iconObj.selected4 : iconObj.selected : iconObj.unselected} style={selectedLog[id] > 3 ? icon === 'flames' ? {'--fa-primary-color': iconObj.checked, '--fa-secondary-color': iconObj.checked2, '--fa-secondary-opacity': '1'} : icon === 'emotions' ? {color: iconObj.checked4} : {color: iconObj.checked} : null} />
                </div>
                <div id='icon-5' className={selectedLog[id] === 5 ? `${styles.iconRating} ${styles.selected}` : styles.iconRating}>
                    <i className={selectedLog[id] > 4 ? icon === 'emotions' ? iconObj.selected5 : iconObj.selected : iconObj.unselected} style={selectedLog[id] > 4 ? icon === 'flames' ? {'--fa-primary-color': iconObj.checked, '--fa-secondary-color': iconObj.checked2, '--fa-secondary-opacity': '1'} : icon === 'emotions' ? {color: iconObj.checked5} : {color: iconObj.checked} : null} />
                </div>
            </div>
        </div>
    );
};

const OpinionScale = ({ field, selectedLog }) => {
    const { id, name, required, scale } = field;

    const numbers = () => {
        let numbs = [];
        let spread = scale ? (scale > 10 ? 10 : scale) : 5;
        for (let i = 1; i <= spread; i++) {
            numbs.push(i);
        };

        return (
            <>
            {numbs.filter(n => n !== 10).map((n,i) => (
                <button id={`radio-${n}`} key={i} className={selectedLog[id] === n ?  `${styles.opinion} ${styles.selected}` : styles.opinion}>
                    <i className={`far fa-${n}`} style={{pointerEvents: 'none'}} />
                </button>
            ))}
            {numbs.filter(n => n === 10).map((n,i) => (
                <button id={`radio-${n}`} key={n} className={selectedLog[id] === n ? ` ${styles.opinion} ${styles.selected}` : styles.opinion}>
                    <i className='far fa-1' style={{pointerEvents: 'none'}} />
                    <i className='far fa-0' style={{pointerEvents: 'none'}} />
                </button>
            ))}
            </>
        );
    };

    return (
        <div className={styles.inputGroup}>
            <label className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <div className={styles.opinions}>
                {numbers()}
            </div>
        </div>
    );
};

const Header = ({ field }) => {
    const { name } = field;
    const defaultStyles = {
        fontSize: '2rem',
        fontWeight: '700',
        textAlign: 'center'
    };
    const appliedStyles = {
        ...defaultStyles,
        ...(field.styles || {}),
        margin: 0
    };
    return (
        <div className={styles.inputGroup}>
            <h1 className={styles.readonlyText} style={appliedStyles}>{name || ''}</h1>
        </div>
    );
};

const Paragraph = ({ field }) => {
    const { name } = field;
    const defaultStyles = {
        fontSize: '16px',
        textAlign: 'center'
    };
    const appliedStyles = {
        ...defaultStyles,
        ...(field.styles || {})
    };
    return (
        <div className={styles.inputGroup}>
            <span className={styles.readonlyTextarea} style={appliedStyles}>{name || ''}</span>
        </div>
    );
};

const HeaderParagraph = ({ field }) => {
    const nameValue = field?.name && typeof field.name === 'object'
        ? field.name
        : { header: '', paragraph: '' };
    const styleGroups = field?.styles && typeof field.styles === 'object'
        ? field.styles
        : {};
    const sharedStyles = Object.fromEntries(
        Object.entries(styleGroups).filter(([key]) => !['header', 'paragraph'].includes(key))
    );
    const headerStyles = {
        fontSize: '2rem',
        fontWeight: '700',
        textAlign: 'center',
        ...sharedStyles,
        ...(styleGroups.header || {}),
        margin: 0
    };
    const paragraphStyles = {
        fontSize: '16px',
        textAlign: 'center',
        ...sharedStyles,
        ...(styleGroups.paragraph || {})
    };

    return (
        <div className={styles.inputGroup}>
            <div className={styles.stackedTextBlock}>
                <h1 className={styles.readonlyText} style={headerStyles}>{nameValue.header || ''}</h1>
                <span className={styles.readonlyTextarea} style={paragraphStyles}>{nameValue.paragraph || ''}</span>
            </div>
        </div>
    );
};

const Divider = (_) => {
    return (
        <div className={styles.inputGroup}>
            <hr className={styles.divider} />
        </div>
    );
};

const SlidingScale = ({ field, selectedLog }) => {
    const { id, name, required } = field;
    const value = selectedLog[id] ?? 50;
    
    return (
        <div className={styles.inputGroup}>
            <label className={styles.label}>{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', width: '100%' }}>
                <div className={styles.slidingScale}>
                    <input 
                        type='range' 
                        min='0' 
                        max='100' 
                        value={value}
                        disabled
                        readOnly
                    />
                </div>
                <div style={{ minWidth: '40px', textAlign: 'center', fontWeight: 'bold', color: '#607ca7' }}>{value}</div>
              </div>
        </div>
    );
};

const Banner = ({ field }) => {
    const { name, options } = field;
    const showIcon = options && options.includes('show-icon');
    const bannerColor = field.styles?.color || '#607ca7';
    const bannerBgAlpha = (hex, alpha = 0.12) => {
        let c = (hex || '').replace('#', '').trim();
        if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
        if (c.length !== 6) return `rgba(96, 124, 167, ${alpha})`;
        const num = parseInt(c, 16);
        if (isNaN(num)) return `rgba(96, 124, 167, ${alpha})`;
        const r = (num >> 16) & 255;
        const g = (num >> 8) & 255;
        const b = num & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    const bannerBackground = bannerBgAlpha(bannerColor, 0.12);
    const defaultStyles = {
        backgroundColor: bannerBackground,
        border: `2px solid ${bannerColor}`,
        color: bannerColor,
        textAlign: 'left'
    };
    const appliedStyles = {
        ...defaultStyles,
        ...(field.styles || {})
    };
    const iconColor = field.styles?.color || '#607ca7';
    return (
        <div className={styles.inputGroup}>
            <div className={styles.banner} style={appliedStyles}>
                {showIcon && <i className='far fa-lightbulb-on' style={{ color: iconColor, fontSize: '0.75em', marginRight: '0.5rem', flexShrink: 0 }} />}
                <span>{name || ''}</span>
            </div>
        </div>
    );
};

const FileDisplay = ({ field, selectedLog }) => {
    const { id, name, required } = field;

    const fileVal = Array.isArray(selectedLog?.[id]) ? selectedLog?.[id]?.[0] : selectedLog?.[id];
    const fileUrl = fileVal?.url;
    const fileName = fileVal?.name;

    return (
        <div className={styles.inputGroup}>
            <label className={styles.label}>{required ? <>{name}<sup><i className='far fa-asterisk' /></sup></> : name}</label>
            {fileUrl ? (
                <a href={fileUrl} target='_blank' rel='noreferrer' className={styles.imgWrap}>
                    <img src={fileUrl} alt={fileName || 'File'} />
                </a>
            ) : (
                <p>No file uploaded</p>
            )}
        </div>
    );
};

export default function FieldTypesView({ configTypes, field, sigCanvas, setSigCanvas, selectedLog }) {
    const conf = configTypes?.[field?.type] || {};
    switch (conf?.type) {
        case 'text':
            return <TextBox field={field} selectedLog={selectedLog} />;
        case 'email':
            return <Email field={field} selectedLog={selectedLog} />;
        case 'phone':
            return <Phone field={field} selectedLog={selectedLog} />;
        case 'address':
            return <Address field={field} selectedLog={selectedLog} />;
        case 'textarea':
            return <TextArea field={field} selectedLog={selectedLog} />;
        case 'number':
            return <Number field={field} selectedLog={selectedLog} />;
        case 'select':
            return <Dropdown field={field} selectedLog={selectedLog} />;
        case 'signature':
            return <Signature field={field} sigCanvas={sigCanvas} setSigCanvas={setSigCanvas} selectedLog={selectedLog} />;
        case 'radio':
            return <SingleSelect field={field} selectedLog={selectedLog} />;
        case 'icon-rating':
            return <IconRating field={field} selectedLog={selectedLog} />;
        case 'num-rating':
            return <OpinionScale field={field} selectedLog={selectedLog} />;
        case 'checkbox':
            if (conf?.multiple) {
                return <Checklist field={field} selectedLog={selectedLog} />
            } else {
                return <Checkbox field={field} selectedLog={selectedLog} />;
            };
        case 'datetime-local':
            return <Timestamp field={field} isTimestamp={conf.type === 'date'} selectedLog={selectedLog} />;
        case 'header':
            return <Header field={field} selectedLog={selectedLog} />;
        case 'paragraph':
            return <Paragraph field={field} selectedLog={selectedLog} />;
        case 'headerParagraph':
            return <HeaderParagraph field={field} selectedLog={selectedLog} />;
        case 'divider':
            return <Divider field={field} selectedLog={selectedLog} />;
        case 'banner':
            return <Banner field={field} selectedLog={selectedLog} />;
        case 'slide-scale':
            return <SlidingScale field={field} selectedLog={selectedLog} />;
        case 'file':
            return <FileDisplay field={field} selectedLog={selectedLog} />;
        default:
            return <></>;
    };
};
